import axios from "axios";
import type {
  AuthUser,
  Tenant,
  InvitationSummary,
  CreatedInvitation,
  InvitePreview,
  EmailResult,
} from "./types";

const API_BASE = process.env.REACT_APP_API_V2_URL || "";

const authClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export interface LoginResponse {
  user: AuthUser;
  tenants: Tenant[];
  active_tenant: Tenant;
}

export interface MeResponse {
  user: AuthUser;
  tenants: Tenant[];
  active_tenant: Tenant | null;
}

export async function login(
  email: string,
  password: string
): Promise<LoginResponse> {
  const { data } = await authClient.post("/api/v2/auth/login", {
    email,
    password,
  });
  return data;
}

export async function logout(): Promise<void> {
  await authClient.post("/api/v2/auth/logout");
}

export async function fetchMe(): Promise<MeResponse> {
  const { data } = await authClient.get("/api/v2/auth/me");
  return data;
}

export async function switchTenant(
  slug: string
): Promise<{ active_tenant: Tenant }> {
  const { data } = await authClient.post("/api/v2/auth/switch-tenant", {
    slug,
  });
  return data;
}

// --- Admin API ---

export interface AdminUser extends AuthUser {
  tenants: Tenant[];
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const { data } = await authClient.get("/api/v2/admin/users");
  return data;
}

export async function createUser(params: {
  email: string;
  password: string;
  name: string;
  is_admin?: boolean;
}): Promise<AuthUser> {
  const { data } = await authClient.post("/api/v2/admin/users", params);
  return data;
}

export async function fetchAdminTenants(): Promise<Tenant[]> {
  const { data } = await authClient.get("/api/v2/admin/tenants");
  return data;
}

export async function createTenant(params: {
  display_name: string;
  slug?: string;
}): Promise<Tenant> {
  const { data } = await authClient.post("/api/v2/admin/tenants", params);
  return data;
}

export async function assignUserToTenant(params: {
  user_id: number;
  tenant_id: number;
  role?: string;
}): Promise<{ user_id: number; tenant_id: number; role: string }> {
  const { data } = await authClient.post("/api/v2/admin/assign", params);
  return data;
}

// --- Invitations (admin) ---

export async function fetchInvitations(): Promise<InvitationSummary[]> {
  const { data } = await authClient.get("/api/v2/admin/invitations");
  return data;
}

export async function createInvitation(params: {
  email: string;
  name?: string;
  tenant_id: number;
  role?: string;
  is_platform_admin?: boolean;
}): Promise<CreatedInvitation> {
  const { data } = await authClient.post("/api/v2/admin/invitations", params);
  return data;
}

/** Deliverability probe: send the real branded invite email to an existing
 *  address with a dead link (no invitation created). For spam-filter testing. */
export async function sendTestInvitationEmail(params: {
  email: string;
  name?: string;
  tenant_id?: number;
}): Promise<{ email_result: EmailResult; accept_url: string }> {
  const { data } = await authClient.post(
    "/api/v2/admin/invitations/test-email",
    params
  );
  return data;
}

export async function revokeInvitation(id: number): Promise<InvitationSummary> {
  const { data } = await authClient.delete(`/api/v2/admin/invitations/${id}`);
  return data;
}

export async function resendInvitation(id: number): Promise<CreatedInvitation> {
  const { data } = await authClient.post(
    `/api/v2/admin/invitations/${id}/resend`
  );
  return data;
}

// --- Invitations (public) ---

export async function fetchInvite(token: string): Promise<InvitePreview> {
  const { data } = await authClient.get(`/api/v2/auth/invite/${token}`);
  return data;
}

export async function claimInvite(params: {
  token: string;
  name: string;
  password: string;
}): Promise<LoginResponse> {
  const { data } = await authClient.post("/api/v2/auth/claim-invite", params);
  return data;
}
